/**
 * Pure cart-row helpers. No money is computed here — the shipping ledger is
 * server-authoritative (see server/api-util/cartShipping.js).
 *
 * A persisted row holds five fields and nothing else:
 *
 *   { listingId, qty, deliveryMethod, unitPriceAtAdd, addedAt }
 *
 * Title, image, live price, stock, shipping fees, packing class, pickup origin and
 * seller all come from the revalidate endpoint's live listing fetch. Persisting any
 * of them would recreate the stale-price bug in every place it was copied to.
 * `unitPriceAtAdd` is kept purely to render the old-vs-new price diff.
 */

import { coerceMoneyAmount, toListingIdString } from './watchedListings';

/** `currentUser.attributes.profile.privateData` key holding the account cart. */
export const CART_ROWS_KEY = 'cartRows';

/**
 * Same cap as the watchlist, and duplicated as MAX_CART_ROWS in
 * server/api/cart-revalidate.js across the ESM/CJS boundary — change both. 100 is also
 * Sharetribe's listings.query page limit, which is what lets a full cart be revalidated
 * in one request, so raising this breaks that query too.
 */
export const MAX_CART_ROWS = 100;

export const CART_LIMIT_REACHED = 'CART_LIMIT_REACHED';

export const DELIVERY_METHOD_SHIPPING = 'shipping';
export const DELIVERY_METHOD_PICKUP = 'pickup';

/** Tank services and make-offer flows keep their current checkout and never enter the cart. */
export const CART_ELIGIBLE_LISTING_TYPES = ['live_goods', 'dry_goods', 'reef_art_apparel'];

/**
 * @param {string|null|undefined} listingType
 * @returns {boolean}
 */
export const isCartEligibleListingType = listingType =>
  CART_ELIGIBLE_LISTING_TYPES.includes(listingType);

/**
 * @param {Object|null|undefined} listing
 * @returns {boolean}
 */
export const isCartEligibleListing = listing =>
  isCartEligibleListingType(listing?.attributes?.publicData?.listingType);

const toQty = value =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : 1;

const toDeliveryMethod = value =>
  value === DELIVERY_METHOD_PICKUP ? DELIVERY_METHOD_PICKUP : DELIVERY_METHOD_SHIPPING;

const toUnitPrice = value =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;

/**
 * Coerce anything read from storage into the persisted row shape, or null when it
 * cannot be a row at all.
 *
 * @param {Object|null|undefined} row
 * @returns {{ listingId: string, qty: number, deliveryMethod: string, unitPriceAtAdd: number|null, addedAt: string|null }|null}
 */
export const normalizeCartRow = row => {
  if (!row) {
    return null;
  }
  const rawId = toListingIdString(row.listingId);
  const listingId = typeof rawId === 'string' ? rawId.trim() : '';
  if (!listingId) {
    return null;
  }
  return {
    listingId,
    qty: toQty(row.qty),
    deliveryMethod: toDeliveryMethod(row.deliveryMethod),
    unitPriceAtAdd: toUnitPrice(row.unitPriceAtAdd),
    addedAt: typeof row.addedAt === 'string' ? row.addedAt : null,
  };
};

const normalizeCartRows = rows =>
  (Array.isArray(rows) ? rows : []).map(normalizeCartRow).filter(Boolean);

/**
 * De-duplicate by listing id, keeping the first occurrence: one listing is one row.
 */
const dedupeByListingId = rows => {
  const seen = new Set();
  return rows.filter(row => {
    if (seen.has(row.listingId)) {
      return false;
    }
    seen.add(row.listingId);
    return true;
  });
};

/**
 * Build a persistable row from a listing and the buyer's choices on the order rail.
 *
 * @param {Object} listing
 * @param {Object} options
 * @param {number} [options.qty]
 * @param {string} [options.deliveryMethod]
 * @param {string} [options.addedAt] ISO timestamp; defaults to now
 * @returns {Object|null} null when the listing cannot be carted
 */
export const createCartRow = (listing, options = {}) => {
  const listingId = toListingIdString(listing?.id);
  if (!listingId || !isCartEligibleListing(listing)) {
    return null;
  }
  const { qty, deliveryMethod, addedAt = new Date().toISOString() } = options;
  return normalizeCartRow({
    listingId,
    qty,
    deliveryMethod,
    unitPriceAtAdd: coerceMoneyAmount(listing?.attributes?.price?.amount),
    addedAt,
  });
};

/**
 * @param {Object|null|undefined} currentUser
 * @returns {Array<Object>}
 */
export const getCartRows = currentUser =>
  normalizeCartRows(currentUser?.attributes?.profile?.privateData?.[CART_ROWS_KEY]);

/**
 * Unique listings in the cart. Used for the row cap, not the topbar badge.
 *
 * @param {Array<Object>|null|undefined} rows
 * @returns {number}
 */
export const cartRowCount = rows => (Array.isArray(rows) ? rows.length : 0);

/**
 * Total units in the cart (sum of qty). The topbar badge and cart header use this
 * so 4 clams counts as 4, not as 1 listing.
 *
 * @param {Array<Object>|null|undefined} rows
 * @returns {number}
 */
export const cartItemCount = rows =>
  Array.isArray(rows)
    ? rows.reduce((sum, row) => {
        const qty = Number(row?.qty);
        return sum + (Number.isInteger(qty) && qty > 0 ? qty : 0);
      }, 0)
    : 0;

/**
 * @param {Array<Object>} rows
 * @param {string|{uuid: string}} listingId
 * @returns {Object|null}
 */
export const findCartRow = (rows, listingId) => {
  const idStr = toListingIdString(listingId);
  if (!idStr) {
    return null;
  }
  return normalizeCartRows(rows).find(r => r.listingId === idStr) || null;
};

/**
 * Add a row, or update the row that listing already has. Re-adding replaces quantity,
 * delivery method and `unitPriceAtAdd` but keeps the original `addedAt`, so an update
 * does not jump the queue when the cap has to drop device rows on merge.
 *
 * @param {Array<Object>} currentRows
 * @param {Object} row
 * @returns {{ nextRows: Array<Object>, added: boolean } | { error: string }}
 */
export const computeNextCartRows = (currentRows, row) => {
  const nextRow = normalizeCartRow(row);
  if (!nextRow) {
    return { error: 'CART_INVALID_ROW' };
  }

  const rows = dedupeByListingId(normalizeCartRows(currentRows));
  const existingIndex = rows.findIndex(r => r.listingId === nextRow.listingId);

  if (existingIndex >= 0) {
    const existing = rows[existingIndex];
    const nextRows = [...rows];
    nextRows[existingIndex] = { ...nextRow, addedAt: existing.addedAt || nextRow.addedAt };
    return { nextRows, added: false };
  }

  if (rows.length >= MAX_CART_ROWS) {
    return { error: CART_LIMIT_REACHED };
  }

  return { nextRows: [nextRow, ...rows], added: true };
};

/**
 * @param {Array<Object>} currentRows
 * @param {string|{uuid: string}} listingId
 * @returns {Array<Object>}
 */
export const removeCartRow = (currentRows, listingId) => {
  const idStr = toListingIdString(listingId);
  const rows = normalizeCartRows(currentRows);
  return idStr ? rows.filter(r => r.listingId !== idStr) : rows;
};

/**
 * Drop every listing in a paid box in a single persist, so sibling boxes stay.
 *
 * @param {Array<Object>} currentRows
 * @param {Array<string|{uuid: string}>} listingIds
 * @returns {Array<Object>}
 */
export const removeCartRows = (currentRows, listingIds) => {
  const drop = new Set(
    (Array.isArray(listingIds) ? listingIds : []).map(toListingIdString).filter(Boolean)
  );
  if (drop.size === 0) {
    return normalizeCartRows(currentRows);
  }
  return normalizeCartRows(currentRows).filter(r => !drop.has(r.listingId));
};

const mapRow = (currentRows, listingId, mapper) => {
  const idStr = toListingIdString(listingId);
  return normalizeCartRows(currentRows).map(r =>
    r.listingId === idStr ? normalizeCartRow(mapper(r)) : r
  );
};

/**
 * @param {Array<Object>} currentRows
 * @param {string|{uuid: string}} listingId
 * @param {number} qty
 * @returns {Array<Object>}
 */
export const setCartRowQty = (currentRows, listingId, qty) =>
  mapRow(currentRows, listingId, r => ({ ...r, qty }));

/**
 * Switching a row's delivery method moves it into that seller's other box.
 *
 * @param {Array<Object>} currentRows
 * @param {string|{uuid: string}} listingId
 * @param {string} deliveryMethod
 * @returns {Array<Object>}
 */
export const setCartRowDeliveryMethod = (currentRows, listingId, deliveryMethod) =>
  mapRow(currentRows, listingId, r => ({ ...r, deliveryMethod }));

const byAddedAtDesc = (a, b) => {
  const aTime = a.addedAt ? Date.parse(a.addedAt) : NaN;
  const bTime = b.addedAt ? Date.parse(b.addedAt) : NaN;
  const aValid = Number.isFinite(aTime);
  const bValid = Number.isFinite(bTime);
  if (aValid && bValid) {
    return bTime - aTime;
  }
  // Rows with no usable timestamp sort last but keep their relative order.
  return aValid ? -1 : bValid ? 1 : 0;
};

/**
 * Merge the device cart into the account cart at login.
 *
 * Union by listing id; on conflict the account row wins, keeping its quantity and
 * delivery method. Account rows are kept first, then new device rows newest-first,
 * and anything past the cap is dropped and counted.
 *
 * The returned summary is exactly what the merge notice reports — the UI must not
 * re-derive it. Stock clamps are deliberately absent: they surface later as ordinary
 * `stock_reduced` row flags, so the same fact is never told twice.
 *
 * @param {Array<Object>} deviceRows
 * @param {Array<Object>} accountRows
 * @returns {{ rows: Array<Object>, summary: { carriedOver: number, conflicts: string[], droppedForCap: number } }}
 */
export const mergeCarts = (deviceRows, accountRows) => {
  const account = dedupeByListingId(normalizeCartRows(accountRows));
  const device = dedupeByListingId(normalizeCartRows(deviceRows));

  const accountIds = new Set(account.map(r => r.listingId));
  const conflicts = device.filter(r => accountIds.has(r.listingId)).map(r => r.listingId);
  const newDeviceRows = device.filter(r => !accountIds.has(r.listingId)).sort(byAddedAtDesc);

  const combined = [...account, ...newDeviceRows];
  const rows = combined.slice(0, MAX_CART_ROWS);

  return {
    rows,
    summary: {
      carriedOver: rows.filter(r => !accountIds.has(r.listingId)).length,
      conflicts,
      droppedForCap: combined.length - rows.length,
    },
  };
};
